import { MINIMIZE_TIME } from '@/common/config';
import { emitAppFloatButton, emitFloatButton, emitOpenArticle, emitOpenMyRequest, EventBus } from '@/common/eventBus';
import { LoginResult } from '@/common/types';
import Vue from 'vue';
import { store } from '@/store';
import { Conversation } from '@/views/Chatroom/active-conversations/type';
import { getParentPostMessageTargetOrigin } from '@/common/utils/getParentPostMessageTargetOrigin';
import {
  DEEP_LINK_URL_POSTFIX,
  isFirstVersionArticleAction,
  PLEASED_ARTICLE_APP_ACTION,
  PLEASED_ARTICLE_DEEP_LINKS,
  PleasedArticleAction,
  SHORTCUT_ARTICLE_ACTIONS,
} from '@/common/bot/flow-frameworks/ChatMessageActions.static';
import { logger } from '@/common/api/logger';
import * as env from '@/common/environment';
import { randomId } from '@/common/bot/lib';
import type { BridgeService, ParentEventData } from '@/common/bridge/types';
import { platform, availableReceiveEvents, listenFromParent, SPORTY_DESK, SPORTY_BET } from '@/common/bridge/common';
import { devLog } from '@/common/debug';
import { analytics, GENERAL_EVENTS } from '../logs';

// TODO: [Sportybet]: 1. 'INIT' shouldn't pass token from new client 2. Is it proper pleased-chat-bot get LOGIN for username and password ?
const availableAndroidSendEvents = ['APP_HAS_VOICE_PERMISSION', 'APP_GO_SETTING', 'START_CALL', 'NATIVE_END_CALL'];
const availableNativeSendEvents = ['NEW_MESSAGE', 'CLEAR_FLOAT'];
const availableSendEvents = [
  'INIT',
  'LOGIN',
  'MINIMIZE',
  'LOGIN_POP',
  'CLOSE',
  'GO_TO_ACTION',
  // Web/WAP only: new auth + SB API proxy flow
  'GET_JWE_TOKEN',
  'CALL_PARENT_API',
  ...availableAndroidSendEvents,
  availableNativeSendEvents,
];

// GO_LIVE_CHAT -> deprecated
// Android ONLY: 'APP_HAS_VOICE_PERMISSION', 'APP_GO_SETTING', 'APP_HAS_VOICE_PERMISSION'
// Android / iOS ONLY: 'NEW_MESSAGE', 'CLEAR_FLOAT'

const BridgeEventsBus = new Vue();
/**
 * SPEC:
 * {
 *  appName: 'SPORTY_DESK',
 *  eventType: string
 * }
 *
 *  eventType: 'LOGIN'
 *  eventType: 'MINIMIZE'
 */
export const sendToParent = (eventType: string, data: any = {}) => {
  if (!availableSendEvents.includes(eventType)) return;
  BridgeEventsBus.$emit('SENDING_EVENT_TO_PARENT', { eventType, data });
  switch (platform) {
    case 'wap':
    case 'web':
      const targetOrigin = getParentPostMessageTargetOrigin();

      if (!targetOrigin) {
        devLog.warn('[LEGACY_BRIDGE] Could not resolve parent origin.');
        break;
      }

      window.parent.postMessage(
        {
          appName: SPORTY_DESK,
          eventType,
          data,
        },
        targetOrigin
      );
      break;
    case 'app':
      const androidResult = JSON.stringify({
        appName: SPORTY_DESK,
        eventType,
        data,
      });
      const androidInterface = window.pleased_bridge || window.app_receiver;
      androidInterface?.sendCmd(androidResult);
      break;
    case 'ios':
      const iosResult = JSON.stringify({
        appName: SPORTY_DESK,
        eventType,
        data,
      });
      // IMPORTANT: app_recevier is correct name here, do no change it
      const iosInterface =
        window.webkit &&
        window.webkit.messageHandlers &&
        (window.webkit.messageHandlers.pleased_bridge || window.webkit.messageHandlers?.app_recevier);
      iosInterface?.postMessage(iosResult);
      break;
    default:
  }
};

/**
 * SPEC:
 * {
 *   appName: 'SPORTY_BET',
 *   eventType: string,
 *   data: any
 * }
 *
 *  eventType: 'INIT',
 *  data: {
 *    isLogin: boolean,
 *    token: string || null,
 *    countryCode: string
 *  }
 *
 *  eventType: 'LOGIN',
 *  data: {
 *    token: string
 *    errCode: 0
 *  }
 */

// For App
const stripPointScoreField = (raw: string): string => {
  const key = '"pointScore":"';
  const keyIndex = raw.indexOf(key);
  if (keyIndex < 0) return raw;

  const valueStart = keyIndex + key.length;
  if (raw[valueStart] !== '[') return raw;

  const closeIndex = raw.indexOf(']"', valueStart);
  if (closeIndex < 0) return raw;

  let before = raw.slice(0, keyIndex);
  let after = raw.slice(closeIndex + 2);

  // Handle commas around removed field to keep JSON valid.
  const beforeHasComma = before.endsWith(',');
  const afterHasComma = after.startsWith(',');
  if (afterHasComma) after = after.slice(1);
  else if (beforeHasComma) before = before.slice(0, -1);

  return `${before}${after}`;
};

const parseParentEventDataFromRaw = (raw: string): { event: ParentEventData; raw: string } | null => {
  try {
    return { event: JSON.parse(raw) as ParentEventData, raw };
  } catch {
    // Sporty sometimes sends malformed JSON when `pointScore` contains quotes inside a string.
    // We do not use `pointScore`, so if parsing fails, remove it and retry.
    const stripped = stripPointScoreField(raw);
    if (stripped === raw) return null;
    try {
      return { event: JSON.parse(stripped) as ParentEventData, raw: stripped };
    } catch {
      return null;
    }
  }
};

export const registerEventFromApp = () => {
  if ((platform !== 'app' && platform !== 'ios') || (<any>window).sportydesk_receiver) return;
  (<any>window).sportydesk_receiver = {};
  (<any>window).sportydesk_receiver.sendCmd = function (info: string) {
    const parsed = parseParentEventDataFromRaw(info);
    if (!parsed) {
      devLog.warn('[LEGACY_BRIDGE] Failed to parse cmd from native', { platform });
      return;
    }
    const { event, raw } = parsed;
    if (event.appName !== SPORTY_BET || !availableReceiveEvents.includes(event.eventType)) return;

    // Keep existing contract: downstream listener expects a JSON string.
    EventBus.$emit(`Bridge_${event.eventType}`, raw);
  };
};

/**
 * Bridge service. TODO: Migrate all options here.
 */
export class LegacyBridge implements BridgeService {
  isMinimized = false;
  platform = platform;
  isLegacyBridge = true;

  userRedirected = false;
  eventsStack: any[] = [];

  constructor() {
    // Setup listeners.
    BridgeEventsBus.$on('SENDING_EVENT_TO_PARENT', ({ eventType, data }: { eventType: string; data: any }) => {
      if (eventType === 'MINIMIZE') this.isMinimized = !data?.open;
    });

    listenFromParent('INIT', (data) => this.eventsStack.push(data));
    listenFromParent('OPEN', (data) => this.eventsStack.push(data));
  }

  sendToParent(eventType: string, data: any = {}) {
    sendToParent(eventType, data);
  }

  listenFromParent<T>(eventType: string, callback: (data: T) => void): () => void {
    return listenFromParent<T>(eventType, callback);
  }

  login(data: { username: string; password: string }) {
    store.commit('user/setBridgeWidgetLoginInProgress', true);
    return new Promise<LoginResult>((resolve) => {
      sendToParent('LOGIN', data);
      const removeListener = listenFromParent('LOGIN', (...args: [LoginResult]) => {
        removeListener();
        resolve(...args);
      });
    }).finally(() => {
      store.commit('user/setBridgeWidgetLoginInProgress', false);
    });
  }

  /**
   * Web/WAP only. Ask the host (Sportybet/F-com page) to generate a JWE token
   * via its backend callback and return it to the iframe.
   */
  getJweToken() {
    return new Promise<string>((resolve) => {
      // Trigger host to call callbacks.token(), then host pushes TOKEN { value }.
      sendToParent('GET_JWE_TOKEN');

      // Host responds with TOKEN and data.value
      const removeTokenListener = listenFromParent('TOKEN', (res) => {
        const token = res?.value || '';
        if (!token) return;
        removeTokenListener();
        resolve(token);
      });
    });
  }

  /**
   * Web/WAP only. Proxy Sportybet API calls through the host page.
   */
  callParentApi<T>(params: { url: string; method: string; json: Record<string, T>; headers?: Record<string, string> }) {
    const requestId = randomId(16);

    return new Promise((resolve, reject) => {
      const removeListener = listenFromParent('CALLPARENTAPI', (res) => {
        if (res?.requestId && res.requestId !== requestId) return;
        removeListener();
        const error = res?.error;
        if (error) return reject(new Error(error));
        return resolve(res?.result);
      });

      sendToParent('CALL_PARENT_API', { requestId, ...params });
    });
  }

  minimize({ avoidNoop = false, inCall = false }: { avoidNoop?: boolean; inCall?: boolean } = {}) {
    const { platform, isMinimized } = this;

    // We want to redirect user to last event page when user minimize the chat & opens later
    this.userRedirected = false;

    if (isMinimized && avoidNoop) return;
    if (platform === 'wap' || platform === 'web') {
      emitFloatButton(true);
      store.commit('chat/updateNewMessage', false);
    } else {
      emitAppFloatButton(true);
    }

    analytics.track({
      name: GENERAL_EVENTS.CHATBOT_MINIMIZE,
      metadata: { platform, isMinimized, inCall, avoidNoop },
    });

    sendToParent('MINIMIZE', {
      open: false,
      period: MINIMIZE_TIME,
      ...(inCall ? { inCall } : {}),
    });
  }

  maximize({ avoidNoop = false }: { avoidNoop?: boolean } = {}) {
    const { platform, isMinimized } = this;
    if (!isMinimized && avoidNoop) return;
    if (platform === 'wap' || platform === 'web') {
      emitFloatButton(false);
    } else {
      emitAppFloatButton(false);
    }

    sendToParent('MINIMIZE', { open: true, period: MINIMIZE_TIME });
  }

  toggleCover() {
    return this.isMinimized ? this.maximize() : this.minimize();
  }

  goToAction(action?: PleasedArticleAction | null) {
    const { platform } = this;

    if (!action) return;

    const isFirstVersionAction = isFirstVersionArticleAction(action);

    if (platform === 'wap' || platform === 'web' || isFirstVersionAction) {
      sendToParent('GO_TO_ACTION', { action });
    } else {
      const country = env.get('country');
      const { deepLinkUrl, deepLinkShortcut } = env.get('app') || { deepLinkUrl: '', deepLinkShortcut: '' };

      const isShortcutLink = SHORTCUT_ARTICLE_ACTIONS.includes(action);
      const deepLink = isShortcutLink ? deepLinkShortcut : deepLinkUrl;
      const deepLinkPostfix = isShortcutLink ? '' : DEEP_LINK_URL_POSTFIX;

      if (!deepLink || !country) {
        logger.error('[GO_TO_ACTION] No deep link or country found for the action');
        return;
      }

      const pageUrl = `${deepLink}/${country}/${deepLinkPostfix}/${PLEASED_ARTICLE_DEEP_LINKS[action]}`;

      sendToParent('GO_TO_ACTION', { action: PLEASED_ARTICLE_APP_ACTION, page: pageUrl });
    }
  }

  openActiveConversation(ticket: Conversation) {
    BridgeEventsBus.$emit('OPEN_ACTIVE_CONVERSATION', ticket);
  }

  onOpenActiveConversation(handler: (ticket: Conversation) => void) {
    BridgeEventsBus.$on('OPEN_ACTIVE_CONVERSATION', handler);
    return () => BridgeEventsBus.$off('OPEN_ACTIVE_CONVERSATION', handler);
  }

  redirectToLastEventPage() {
    if (!this.eventsStack.length) return;

    const lastEvent = this.eventsStack[this.eventsStack.length - 1];

    if (!lastEvent || this.userRedirected) return;

    this.userRedirected = true;

    if (lastEvent.myRequest) {
      emitOpenMyRequest(true);
    } else if (lastEvent.articleId) {
      emitOpenArticle({ isOpen: true, articleId: lastEvent.articleId, fetchArticle: true });
    }
  }
}
